import axiosInstance from '../../../../core/network/axiosInstance'
import { EntityInfo, FarmerSaleRecord } from '../../domain/entities/FarmerSale'
import {
  FarmerSaleRepository,
  GetFarmerSalesFilters,
  GetFarmerSalesResponse,
  HierarchyOption,
} from '../../domain/repositories/FarmerSaleRepository'

interface RawFarmerSaleItem {
  id: string
  fpo_id?: string
  fpo_name?: string
  retailer_id?: string
  retailer_name?: string
  seller_role?: string
  buyer_type?: string
  farmer_id?: string
  buyer_user_id?: string
  buyer_name?: string
  buyer_phone?: string
  units?: number
  bags?: number
  amount?: number
  unit_price?: number
  paid_amount?: number
  outstanding_amount?: number
  payment_mode?: string
  cow_count?: number
  buffalo_count?: number
  created_at: string
}

interface RawUserOption {
  id: string
  name: string
  business_name?: string
}

type QueryParamValue = string | number | undefined
type QueryParams = Record<string, QueryParamValue>

const isFpoId = (id: string): boolean => {
  const upper = id.toUpperCase()
  return upper.startsWith('FP0') || upper.startsWith('FPO')
}

const resolveSellerEntity = (
  sellerId = '',
  sellerName = '',
  sellerRole = '',
): { fpo: EntityInfo | null; retailer: EntityInfo | null; vlcc: EntityInfo | null } => {
  const role = sellerRole.toLowerCase()
  const info: EntityInfo = { id: sellerId, name: sellerName }

  if (role === 'fpo' || isFpoId(sellerId)) {
    return { fpo: info, retailer: null, vlcc: null }
  }

  if (role === 'retailer' || sellerId.toUpperCase().startsWith('RET')) {
    return { fpo: null, retailer: info, vlcc: null }
  }

  if (role === 'vlcc' || sellerId.toUpperCase().startsWith('VLC')) {
    return { fpo: null, retailer: null, vlcc: info }
  }

  return { fpo: null, retailer: null, vlcc: null }
}

const isVlccBuyer = (type: string, id: string): boolean => {
  if (['vlcc', 'vlcc_point', 'mcc'].includes(type)) {
    return true
  }

  return id.toUpperCase().startsWith('VLC')
}

const resolveBuyerEntity = (
  item: RawFarmerSaleItem,
): { farmer: EntityInfo | null; retailer: EntityInfo | null; vlcc: EntityInfo | null } => {
  const buyerType = (item.buyer_type || '').toLowerCase()
  const buyerId = item.farmer_id || item.buyer_user_id || ''
  const buyerName = item.buyer_name || ''
  const buyerPhone = item.buyer_phone || ''
  const info: EntityInfo = { id: buyerId, name: buyerName, phone: buyerPhone }

  if (buyerType === 'farmer' || Boolean(item.farmer_id) || buyerId.toUpperCase().startsWith('FMR')) {
    return { farmer: info, retailer: null, vlcc: null }
  }

  if (buyerType === 'retailer' || buyerId.toUpperCase().startsWith('RET')) {
    return { farmer: null, retailer: info, vlcc: null }
  }

  if (isVlccBuyer(buyerType, buyerId)) {
    return { farmer: null, retailer: null, vlcc: info }
  }

  if (buyerName || buyerId) {
    return { farmer: info, retailer: null, vlcc: null }
  }

  return { farmer: null, retailer: null, vlcc: null }
}

const resolveUpstreamFpo = (fpoId?: string, fpoName?: string): EntityInfo | null => {
  if (fpoId && fpoName && fpoId !== 'samarth') {
    return { id: fpoId, name: fpoName }
  }

  return null
}

const fallback = (...values: Array<string | undefined | null>): string => {
  for (const val of values) {
    if (val) {
      return val
    }
  }

  return ''
}

const resolveFinancialMetrics = (item: RawFarmerSaleItem) => ({
  units: item.units ?? item.bags ?? 0,
  amount: item.amount || 0,
  samarthPrice: 0,
  fpoSellPrice: 0,
  retailPrice: item.unit_price || 0,
  amountPaid: item.paid_amount || 0,
  amountOutstanding: item.outstanding_amount || 0,
  paymentMode: (item.payment_mode as 'cash' | 'credit' | 'upi') || 'cash',
  cowCount: item.cow_count || 0,
  buffaloCount: item.buffalo_count || 0,
})

const mapRawFarmerSaleItem = (item: RawFarmerSaleItem): FarmerSaleRecord => {
  const seller = resolveSellerEntity(item.retailer_id, item.retailer_name, item.seller_role)
  const buyer = resolveBuyerEntity(item)
  const upstreamFpo = resolveUpstreamFpo(item.fpo_id, item.fpo_name)

  const fpo = seller.fpo || upstreamFpo
  const retailer = seller.retailer || buyer.retailer
  const vlcc = seller.vlcc || buyer.vlcc
  const farmer = buyer.farmer

  return {
    id: item.id,
    fpo,
    retailer,
    vlcc,
    farmer,
    fpoId: fallback(fpo?.id, item.fpo_id, 'samarth'),
    fpoName: fallback(fpo?.name, item.fpo_name, 'Samarth Direct Sale'),
    vlccId: fallback(vlcc?.id, item.retailer_id, 'unknown'),
    vlccName: fallback(vlcc?.name, item.retailer_name, 'N/A'),
    farmerId: fallback(farmer?.id, item.farmer_id, item.buyer_user_id, 'unknown'),
    farmerName: fallback(farmer?.name, item.buyer_name, 'Unknown'),
    farmerPhone: fallback(farmer?.phone, item.buyer_phone, ''),
    createdAt: item.created_at,
    ...resolveFinancialMetrics(item),
  }
}

class FarmerSaleApiDatasource implements FarmerSaleRepository {
  public async getFarmerSales(filters: GetFarmerSalesFilters): Promise<GetFarmerSalesResponse> {
    const params: QueryParams = {
      skip: (filters.page - 1) * filters.limit,
      limit: filters.limit,
    }

    if (filters.search) params.search = filters.search
    if (filters.fpoId) params.fpo_id = filters.fpoId
    if (filters.retailerId) params.retailer_id = filters.retailerId
    if (filters.vlccId) params.vlcc_id = filters.vlccId
    if (filters.paymentMode) params.payment_mode = filters.paymentMode

    const response = await axiosInstance.get('/api/v1/farmers/sales', { params })
    const { data, total } = response.data

    const records: FarmerSaleRecord[] = (data || []).map(mapRawFarmerSaleItem)

    return {
      records,
      totalItems: total || 0,
      totalPages: Math.ceil((total || 0) / filters.limit),
    }
  }

  public async getFarmerDetails(farmerId: string): Promise<FarmerSaleRecord[]> {
    const response = await axiosInstance.get('/api/v1/farmers/sales', {
      params: { farmer_id: farmerId, limit: 100 },
    })

    return (response.data.data || []).map(mapRawFarmerSaleItem)
  }

  public async getFpoOptions(): Promise<HierarchyOption[]> {
    const response = await axiosInstance.get('/api/v1/users', {
      params: { role: 'fpo', limit: 1000 },
    })
    return (response.data.data || []).map((u: RawUserOption) => ({
      id: u.id,
      name: u.business_name || u.name,
    }))
  }

  public async getRetailerOptions(fpoId?: string): Promise<HierarchyOption[]> {
    const params: QueryParams = { role: 'retailer', limit: 1000 }
    if (fpoId) params.fpo_id = fpoId
    const response = await axiosInstance.get('/api/v1/users', { params })
    return (response.data.data || []).map((u: RawUserOption) => ({
      id: u.id,
      name: u.business_name || u.name,
    }))
  }

  public async getVlccOptions(fpoId?: string): Promise<HierarchyOption[]> {
    const params: QueryParams = { role: 'vlcc', limit: 1000 }
    if (fpoId) params.fpo_id = fpoId
    const response = await axiosInstance.get('/api/v1/users', { params })
    return (response.data.data || []).map((u: RawUserOption) => ({
      id: u.id,
      name: u.business_name || u.name,
    }))
  }
}

export const apiFarmerSaleDatasource = new FarmerSaleApiDatasource()
